import React, { useState, FormEvent } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import CustomModal from '../../common/CustomModal';
import { useAppDispatch } from '../../../store/hook';
import { uploadPrincipalBankStatement } from '../../../features/principal/PrincipalSlice';
import { UploadBankStatementResponse } from '../../../features/principal/types';
import { Bank } from '../../../features/program/ProgramSlice';

interface BankStatementUploadModalProps {
  show: boolean;
  onHide: () => void;
  principalId: string;
  principalBanks: Bank[] | null;
  defaultPrincipalBankId: number | null;
  onUploadSuccess: () => void;
}

const BankStatementUploadModal: React.FC<BankStatementUploadModalProps> = ({
  show,
  onHide,
  principalId,
  principalBanks,
  defaultPrincipalBankId,
  onUploadSuccess,
}) => {
  const dispatch = useAppDispatch();

  const [selectedBankId, setSelectedBankId] = useState<string>(
    defaultPrincipalBankId ? String(defaultPrincipalBankId) : ''
  );
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadBankStatementResponse | null>(null);

  // Keep the default bank selected when it resolves after the modal mounts.
  React.useEffect(() => {
    if (show && defaultPrincipalBankId && !selectedBankId) {
      setSelectedBankId(String(defaultPrincipalBankId));
    }
  }, [show, defaultPrincipalBankId, selectedBankId]);

  const resetState = () => {
    setFile(null);
    setError(null);
    setFormError(null);
    setResult(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormError(null);
    setResult(null);
    setError(null);
    setFile(event.target.files?.[0] || null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setError(null);
    setResult(null);

    if (!selectedBankId) {
      setFormError('Please select a bank.');
      return;
    }
    if (!file) {
      setFormError('Please choose a .xlsx, .xls, or .csv file.');
      return;
    }

    setLoading(true);
    const response = await dispatch(
      uploadPrincipalBankStatement({
        principalId,
        principalBankId: Number(selectedBankId),
        file,
      })
    );
    setLoading(false);

    if (uploadPrincipalBankStatement.fulfilled.match(response)) {
      setResult(response.payload);
      // Refresh the underlying list immediately, even while the summary shows.
      onUploadSuccess();
    } else {
      setError((response.payload as string) || 'Failed to upload bank statement');
    }
  };

  return (
    <CustomModal show={show} onHide={handleClose} title="Upload Bank Statement" width="w-[560px]">
      <div className="w-full">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
            <select
              value={selectedBankId}
              onChange={(e) => {
                setSelectedBankId(e.target.value);
                setFormError(null);
              }}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a bank</option>
              {principalBanks?.map((bank) => (
                <option key={bank.bankId} value={String(bank.bankId)}>
                  {bank.displayName || bank.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statement File (.xlsx, .xls, .csv)
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
            />
            {file && <div className="mt-2 text-xs text-gray-500">Selected file: {file.name}</div>}
          </div>

          {formError && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
              <AlertCircle className="w-5 h-5" />
              <span>{formError}</span>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Upload
            </button>
          </div>
        </form>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 my-4 text-sm">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="mt-5">
            <div className="grid grid-cols-4 gap-3 text-center">
              <div className="rounded-md bg-gray-50 border border-gray-200 py-3">
                <p className="text-lg font-semibold text-gray-900">{result.totalRows}</p>
                <p className="text-xs text-gray-500">Total Rows</p>
              </div>
              <div className="rounded-md bg-green-50 border border-green-200 py-3">
                <p className="text-lg font-semibold text-green-700">{result.storedCount}</p>
                <p className="text-xs text-gray-500">Stored</p>
              </div>
              <div className="rounded-md bg-yellow-50 border border-yellow-200 py-3">
                <p className="text-lg font-semibold text-yellow-700">{result.duplicateCount}</p>
                <p className="text-xs text-gray-500">Duplicates</p>
              </div>
              <div className="rounded-md bg-red-50 border border-red-200 py-3">
                <p className="text-lg font-semibold text-red-700">{result.failedCount}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
            </div>

            {result.failedRows && result.failedRows.length > 0 && (
              <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Row</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.failedRows.map((row) => (
                        <tr key={row.rowNumber} className="border-b border-gray-100">
                          <td className="py-2 px-3 text-gray-700">{row.rowNumber}</td>
                          <td className="py-2 px-3 text-gray-700">{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default BankStatementUploadModal;
